import React from 'react';
import {
  CreditCard,
  Pencil,
  Trash2,
  CheckCircle
} from 'lucide-react';

const PaymentMethodsList = ({ paymentMethods, onEdit, onDelete, onSetDefault }) => {
  // Helper function to get icon and color based on payment method type
  const getMethodInfo = (type) => {
    switch (type) {
      case 'paypal':
        return { 
          icon: 'PP', 
          bgColor: 'bg-blue-100', 
          textColor: 'text-blue-600',
          label: 'PayPal'
        };
      case 'bank':
        return { 
          icon: 'BT', 
          bgColor: 'bg-green-100', 
          textColor: 'text-green-600',
          label: 'Bank Transfer'
        };
      case 'gcash':
        return { 
          icon: 'GC', 
          bgColor: 'bg-purple-100', 
          textColor: 'text-purple-600',
          label: 'GCash'
        };
      default:
        return { 
          icon: 'PM', 
          bgColor: 'bg-gray-100', 
          textColor: 'text-gray-600',
          label: 'Payment Method'
        };
    }
  };

  // Helper function to display payment method details
  const getMethodDetails = (method) => {
    switch (method.type) {
      case 'paypal':
        return method.details.email;
      case 'bank':
        return `${method.details.bankName} - ${method.details.accountNumber?.slice(-4).padStart(method.details.accountNumber?.length, '*')}`;
      case 'gcash':
        return `${method.details.phoneNumber?.slice(0, 3)}****${method.details.phoneNumber?.slice(-4)}`;
      default:
        return '';
    }
  };

  return (
    <div className="space-y-4">
      {paymentMethods.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No payment methods</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add a payment method to receive payments from brands.
          </p>
        </div>
      ) : (
        paymentMethods.map((method) => {
          const { icon, bgColor, textColor, label } = getMethodInfo(method.type);
          
          return (
            <div 
              key={method.id} 
              className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
            >
              <div className="flex items-center">
                <div className={`h-10 w-10 ${bgColor} rounded-full flex items-center justify-center mr-3`}>
                  <span className={`font-bold ${textColor}`}>{icon}</span>
                </div>
                <div>
                  <div className="flex items-center">
                    <h3 className="text-sm font-medium text-gray-900">{label}</h3>
                    {method.isDefault && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{getMethodDetails(method)}</p>
                  <p className="text-xs text-gray-400">Added on {new Date(method.dateAdded).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                {!method.isDefault && (
                  <button
                    onClick={() => onSetDefault(method.id)}
                    className="p-1 rounded-full text-gray-400 hover:text-gray-500"
                    title="Set as default"
                  >
                    <CheckCircle className="h-5 w-5" />
                  </button>
                )}
                <button
                  onClick={() => onEdit(method)}
                  className="p-1 rounded-full text-gray-400 hover:text-gray-500"
                  title="Edit"
                >
                  <Pencil className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onDelete(method.id)}
                  className="p-1 rounded-full text-gray-400 hover:text-red-500"
                  title="Delete"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default PaymentMethodsList;
